import { useCallback, useEffect, useState } from 'react'
import { Bandeau, Zone } from './Champs'
import { Etoiles } from './Etoiles'
import { libelleCritere } from './CarteAvis'
import { jour } from './Bail'
import { ErreurApi } from '../api'
import {
  contesterAvis,
  deposerAvis,
  lireFormulaire,
  lireReputationUtilisateur,
  listerInvitations,
  refuserInvitation,
  repondreAvis,
  signalerAvis,
  type AvisPublie,
  type CritereAvis,
  type FormulaireAvis,
  type InvitationAvis,
  type NatureSujet,
  type NoteCritere,
  type Reputation,
} from '../apiReputation'
import { TEXTES, interpoler, messageErreur, type Langue } from '../i18n'
import { Bloc, Bouton, EtatVide, Icone, Modale, Onglets, Puce, SqueletteListe, SquelettePage } from '../ui'

type Onglet = 'aDeposer' | 'deposes' | 'recus'
type Textes = (typeof TEXTES)['fr']

/**
 * Avis — revu le 15/09/2026 sur la maquette `docs/ui/pwa/avis.html`.
 *
 * <b>Trois onglets, trois temps</b> : ce que je dois, ce que j'ai dit, ce qu'on a dit de moi. Le
 * second porte l'état — <i>en attente de l'autre partie</i> / <i>publié</i> — parce que le double
 * aveugle du §8.2-2 produit un délai que personne ne comprend s'il n'est pas expliqué ; il montre
 * aussi ce que j'ai écrit, sans le rouvrir. Le troisième est ma réputation : la note et son
 * explication (D32), les critères en jauges, chaque avis reçu avec le droit de réponse — une seule
 * réponse, publique et définitive, présentée comme un bloc et non comme un fil —, le signalement,
 * la contestation.
 *
 * <b>La règle est annoncée avant la saisie</b>, pas après : « votre avis ne sera visible que lorsque
 * l'autre partie aura déposé, ou le 14/09 ». Quelqu'un qui découvre le délai après avoir écrit se
 * sent piégé. Le dépôt part avec un identifiant d'invitation, jamais un sujet (conception L8, §1.5).
 */
export function Avis({ langue, moi }: { langue: Langue; moi: string | null }) {
  const t = TEXTES[langue]

  const [onglet, setOnglet] = useState<Onglet>('aDeposer')
  const [invitations, setInvitations] = useState<InvitationAvis[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState<string | null>(null)
  const [occupe, setOccupe] = useState<string | null>(null)

  const charger = useCallback(() => {
    void listerInvitations()
      .then(setInvitations)
      .catch((cause: unknown) => {
        setInvitations([])
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
      })
  }, [langue])

  useEffect(charger, [charger])

  const refuser = async (invitationId: string) => {
    setOccupe(invitationId)

    try {
      await refuserInvitation(invitationId)
      charger()
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setOccupe(null)
    }
  }

  const aDeposer = (invitations ?? []).filter((item) => item.status === 'Pending')
  const deposes = (invitations ?? []).filter((item) => item.status !== 'Pending')

  return (
    <section className="avis">
      <div className="page-entete">
        <h2>{t.avisTitre}</h2>
        <Onglets<Onglet>
          ariaLabel={t.avisTitre}
          actif={onglet}
          onChange={setOnglet}
          onglets={[
            {
              cle: 'aDeposer',
              libelle: (
                <>
                  {t.avisADeposer}
                  {aDeposer.length > 0 && <Puce ton="marque">{aDeposer.length}</Puce>}
                </>
              ),
            },
            { cle: 'deposes', libelle: t.avisDeposes },
            { cle: 'recus', libelle: t.avisRecus },
          ]}
        />
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}

      {onglet !== 'recus' && invitations === null && <SqueletteListe />}

      {onglet === 'aDeposer' && invitations !== null && (
        <div className="pile">
          {aDeposer.length > 0 && (
            <Bandeau ton="info" icone={<Icone nom="oeil" />}>
              <b>{t.avisDoubleAveugleTitre}</b> {t.avisDoubleAveugle}
            </Bandeau>
          )}

          {aDeposer.length === 0 && <EtatVide icone="etoile" titre={t.avisAucunADeposer} />}

          {aDeposer.map((invitation) => (
            <Bloc
              key={invitation.invitationId}
              titre={
                <div>
                  <h3>{titreInvitation(t, invitation)}</h3>
                  <p className="secondaire">{sousTitreInvitation(t, langue, invitation)}</p>
                </div>
              }
              actions={
                <Puce ton="attention">{interpoler(t.avisJusquAu, { date: jour(langue, invitation.closesAt) })}</Puce>
              }
            >
              <div className="rangee rangee--serree">
                <Bouton taille="sm" onClick={() => setEnCours(invitation.invitationId)}>
                  {t.avisNoter}
                </Bouton>
                <Bouton
                  apparence="discret"
                  taille="sm"
                  charge={occupe === invitation.invitationId}
                  onClick={() => void refuser(invitation.invitationId)}
                >
                  {t.avisRefuser}
                </Bouton>
              </div>
              {invitation.isBilateral && <p className="tertiaire avis__aide">{t.avisRefuserAide}</p>}
            </Bloc>
          ))}
        </div>
      )}

      {onglet === 'deposes' && invitations !== null && (
        <div className="pile">
          {deposes.length === 0 && <EtatVide icone="etoile" titre={t.avisAucunDepose} />}

          {deposes.map((invitation) => (
            <Bloc
              key={invitation.invitationId}
              titre={
                <div>
                  <h3>{titreInvitation(t, invitation)}</h3>
                  <p className="secondaire">{sousTitreDepot(t, langue, invitation)}</p>
                </div>
              }
              actions={<Puce ton={tonDepot(invitation)}>{libelleStatut(t, invitation)}</Puce>}
            >
              {invitation.myReview !== null ? (
                <div className="avis__depose">
                  <Etoiles note={invitation.myReview.overallRating} langue={langue} libelle={t.avisReputation} />
                  {invitation.myReview.comment !== null && <p>{invitation.myReview.comment}</p>}
                  {invitation.myReview.publishedAt === null && invitation.isBilateral && (
                    <p className="secondaire">
                      {interpoler(t.avisPublieDes, { date: jour(langue, invitation.blindRevealAt) })}
                    </p>
                  )}
                </div>
              ) : (
                <p className="secondaire">{libelleStatut(t, invitation)}</p>
              )}
            </Bloc>
          ))}
        </div>
      )}

      {onglet === 'recus' && <Recus t={t} langue={langue} moi={moi} onErreur={setErreur} />}

      {enCours !== null && (
        <DeposerAvis
          invitationId={enCours}
          langue={langue}
          onTermine={() => {
            setEnCours(null)
            charger()
          }}
          onAnnule={() => setEnCours(null)}
        />
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Reçus : ma réputation et chaque avis, avec ses droits
// ---------------------------------------------------------------------------

function Recus({
  t,
  langue,
  moi,
  onErreur,
}: {
  t: Textes
  langue: Langue
  moi: string | null
  onErreur: (message: string) => void
}) {
  const [reputation, setReputation] = useState<Reputation | null>(null)
  const [chargement, setChargement] = useState(true)
  const [geste, setGeste] = useState<{ nature: 'repondre' | 'signaler' | 'contester'; avis: AvisPublie } | null>(null)

  const charger = useCallback(() => {
    if (moi === null) {
      setChargement(false)
      return
    }

    void lireReputationUtilisateur(moi, 1, 20)
      .then(setReputation)
      .catch((cause: unknown) => onErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal')))
      .finally(() => setChargement(false))
  }, [langue, moi, onErreur])

  useEffect(charger, [charger])

  if (chargement) {
    return <SquelettePage />
  }

  if (reputation === null) {
    return <EtatVide icone="etoile" titre={t.avisNouveau}>{t.avisNouveauAide}</EtatVide>
  }

  const nombre = new Intl.NumberFormat(langue)
  const note = new Intl.NumberFormat(langue, { minimumFractionDigits: 1, maximumFractionDigits: 1 })

  return (
    <div className="pile">
      <Bloc
        titre={
          <div>
            <h3>{t.avisMaReputation}</h3>
            <p className="secondaire">{t.avisMaReputationAide}</p>
          </div>
        }
        actions={
          reputation.isDisplayable ? (
            <span className="note avis__note">
              <Icone nom="etoile" />
              <span className="note__valeur">{note.format(reputation.displayedRating ?? 0)}</span>
              <span className="note__compte">
                · {nombre.format(reputation.reviewCount)} {t.avisNombre}
              </span>
            </span>
          ) : (
            <Puce>{t.avisNouveau}</Puce>
          )
        }
      >
        {!reputation.isDisplayable ? (
          <p className="secondaire">{t.avisNouveauAide}</p>
        ) : (
          <>
            <div className="criteres">
              {reputation.perCriterion.map((entree) => (
                <div key={entree.criterionCode} className="jauge">
                  <div className="jauge__ligne">
                    <span>{libelleCritere(reputation.criteria, entree.criterionCode, langue)}</span>
                    <span>{note.format(entree.average)}</span>
                  </div>
                  <div className="jauge__piste">
                    <div className="jauge__valeur jauge__valeur--marque" style={{ inlineSize: `${(entree.average / 5) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
            {/* La ligne d'explication de L5, réutilisée : le chiffre affiché doit pouvoir se défendre (D32). */}
            <p className="tertiaire avis__aide">
              {t.avisPondereeAide} ({nombre.format(reputation.halfLifeMonths)}). {t.avisMoyenneBruteLongue}{' '}
              {note.format(reputation.plainAverageRating ?? 0)}.
            </p>
          </>
        )}
      </Bloc>

      {reputation.reviews.items.length > 0 && (
        <Bloc>
          <div className="pile pile--large">
            {reputation.reviews.items.map((avis) => (
              <AvisRecu
                key={avis.id}
                t={t}
                langue={langue}
                avis={avis}
                criteres={reputation.criteria}
                onRepondre={() => setGeste({ nature: 'repondre', avis })}
                onSignaler={() => setGeste({ nature: 'signaler', avis })}
                onContester={() => setGeste({ nature: 'contester', avis })}
              />
            ))}
          </div>
        </Bloc>
      )}

      {geste !== null && (
        <GesteSurAvis
          t={t}
          langue={langue}
          nature={geste.nature}
          avis={geste.avis}
          onFermer={() => setGeste(null)}
          onFait={() => {
            setGeste(null)
            charger()
          }}
          onErreur={onErreur}
        />
      )}
    </div>
  )
}

/** Un avis reçu : l'auteur, la date, les étoiles, le texte, ma réponse si je l'ai donnée, mes gestes. */
function AvisRecu({
  t,
  langue,
  avis,
  criteres,
  onRepondre,
  onSignaler,
  onContester,
}: {
  t: Textes
  langue: Langue
  avis: AvisPublie
  criteres: CritereAvis[]
  onRepondre: () => void
  onSignaler: () => void
  onContester: () => void
}) {
  return (
    <article className="avis-carte">
      <div className="avis-carte__entete">
        <span className="avatar" aria-hidden="true">
          {initiales(avis.authorDisplayName)}
        </span>
        <div>
          <p className="avis-carte__auteur">{avis.authorDisplayName ?? '—'}</p>
          <p className="avis-carte__date">
            {nomAuteur(t, avis.subjectType)} · {jour(langue, avis.publishedAt)}
          </p>
        </div>
        <Etoiles note={avis.overallRating} langue={langue} libelle={t.avisReputation} />
      </div>

      {avis.comment !== null && <p>{avis.comment}</p>}

      {avis.scores.length > 0 && (
        <ul className="avis-carte__criteres">
          {avis.scores.map((score) => (
            <li key={score.criterionCode}>
              <span>{libelleCritere(criteres, score.criterionCode, langue)}</span>
              <Etoiles note={score.score} langue={langue} libelle={libelleCritere(criteres, score.criterionCode, langue)} />
            </li>
          ))}
        </ul>
      )}

      {avis.response !== null && (
        <div className="avis-carte__reponse">
          <p className="fort">{t.avisVotreReponse}</p>
          <p>{avis.response.body}</p>
        </div>
      )}

      <div className="rangee rangee--serree">
        {avis.response === null && (
          <Bouton apparence="discret" taille="sm" onClick={onRepondre}>
            {t.avisRepondre}
          </Bouton>
        )}
        <Bouton apparence="discret" taille="sm" onClick={onSignaler}>
          <Icone nom="drapeau" taille="sm" /> {t.avisSignaler}
        </Bouton>
        <Bouton apparence="discret" taille="sm" onClick={onContester}>
          {t.avisContester}
        </Bouton>
      </div>
    </article>
  )
}

/** Répondre, signaler ou contester : une feuille, un texte, la règle rappelée. */
function GesteSurAvis({
  t,
  langue,
  nature,
  avis,
  onFermer,
  onFait,
  onErreur,
}: {
  t: Textes
  langue: Langue
  nature: 'repondre' | 'signaler' | 'contester'
  avis: AvisPublie
  onFermer: () => void
  onFait: () => void
  onErreur: (message: string) => void
}) {
  const [texte, setTexte] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const titre = nature === 'repondre' ? t.avisRepondre : nature === 'signaler' ? t.avisSignaler : t.avisContester
  const aide = nature === 'repondre' ? t.avisRepondreAide : nature === 'signaler' ? t.avisSignalerAide : t.avisContesterAide

  const envoyer = async () => {
    const corps = texte.trim()

    if (corps === '') return

    setEnvoi(true)

    try {
      if (nature === 'repondre') await repondreAvis(avis.id, corps, langue)
      else if (nature === 'signaler') await signalerAvis(avis.id, 'other', corps)
      else await contesterAvis(avis.id, corps)

      onFait()
    } catch (cause: unknown) {
      onErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
      setEnvoi(false)
    }
  }

  return (
    <Modale
      feuille
      titre={titre}
      onFermer={onFermer}
      pied={
        <>
          <Bouton apparence="discret" onClick={onFermer}>
            {t.annuler}
          </Bouton>
          <Bouton apparence={nature === 'repondre' ? 'principal' : 'danger'} charge={envoi} desactive={texte.trim() === ''} onClick={() => void envoyer()}>
            {titre}
          </Bouton>
        </>
      }
    >
      <Bandeau ton="info">{aide}</Bandeau>
      <Zone id="avis-geste" libelle={t.avisCommentaire} valeur={texte} onChange={setTexte} lignes={5} />
    </Modale>
  )
}

// ---------------------------------------------------------------------------
// Le dépôt, en feuille
// ---------------------------------------------------------------------------

/**
 * Le formulaire de dépôt.
 *
 * <b>Tous les critères sont obligatoires</b> (§8.2-5), le commentaire ne l'est pas. Le bouton reste
 * inactif tant qu'un critère n'est pas noté : refuser à l'envoi ferait perdre le texte écrit.
 */
function DeposerAvis({
  invitationId,
  langue,
  onTermine,
  onAnnule,
}: {
  invitationId: string
  langue: Langue
  onTermine: () => void
  onAnnule: () => void
}) {
  const t = TEXTES[langue]

  const [formulaire, setFormulaire] = useState<FormulaireAvis | null>(null)
  const [notes, setNotes] = useState<Record<string, number>>({})
  const [commentaire, setCommentaire] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    void lireFormulaire(invitationId)
      .then(setFormulaire)
      .catch((cause: unknown) =>
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal')),
      )
  }, [invitationId, langue])

  const envoyer = useCallback(async () => {
    if (!formulaire) {
      return
    }

    setEnvoi(true)
    setErreur(null)

    const scores: NoteCritere[] = formulaire.criteria.map((critere) => ({
      criterionCode: critere.code,
      score: notes[critere.code] ?? 0,
    }))

    try {
      await deposerAvis(invitationId, scores, commentaire.trim() === '' ? null : commentaire.trim(), langue)
      onTermine()
    } catch (cause: unknown) {
      const code = cause instanceof ErreurApi ? cause.code : 'internal'

      // Le code de l'amendement §8.2-3 remonte tel quel : qui a manqué la fenêtre doit comprendre
      // pourquoi, sinon il conclut à un bug et cesse de croire au dispositif.
      setErreur(code === 'REVIEW_WINDOW_CLOSED_AFTER_REVEAL' ? t.avisFenetreFermee : messageErreur(langue, code))
      setEnvoi(false)
    }
  }, [commentaire, formulaire, invitationId, langue, notes, onTermine, t])

  const complet = formulaire !== null && formulaire.criteria.every((critere) => (notes[critere.code] ?? 0) > 0)

  return (
    <Modale
      feuille
      titre={
        formulaire === null
          ? t.avisNoter
          : interpoler(t.avisNoterTitre, {
              sujet: `${nomSujet(t, formulaire.subjectType).toLocaleLowerCase(langue)}${
                formulaire.subjectDisplayName ? ` — ${formulaire.subjectDisplayName}` : ''
              }`,
            })
      }
      onFermer={onAnnule}
      pied={
        <>
          <Bouton apparence="discret" onClick={onAnnule}>
            {t.annuler}
          </Bouton>
          <Bouton charge={envoi} desactive={!complet} onClick={() => void envoyer()}>
            {t.avisDeposer}
          </Bouton>
        </>
      }
    >
      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {formulaire === null && erreur === null && <SqueletteListe />}

      {formulaire !== null && (
        <div className="pile">
          {formulaire.criteria.map((critere) => (
            <div key={critere.code} className="champ avis__critere">
              <span className="champ__libelle">{libelleCritere(formulaire.criteria, critere.code, langue)}</span>
              <Etoiles
                note={notes[critere.code] ?? 0}
                langue={langue}
                libelle={libelleCritere(formulaire.criteria, critere.code, langue)}
                onChange={(note) => setNotes((etat) => ({ ...etat, [critere.code]: note }))}
              />
            </div>
          ))}

          <Zone
            id="avis-commentaire"
            libelle={t.avisCommentaire}
            valeur={commentaire}
            aide={t.avisCommentaireAide}
            lignes={5}
            onChange={setCommentaire}
          />

          {formulaire.isBilateral && (
            <Bandeau ton="info" icone={<Icone nom="oeil" />}>
              {interpoler(t.avisVisibleDes, { date: jour(langue, formulaire.blindRevealAt) })}
            </Bandeau>
          )}
        </div>
      )}
    </Modale>
  )
}

// ---------------------------------------------------------------------------
// Libellés
// ---------------------------------------------------------------------------

function titreInvitation(t: Textes, invitation: InvitationAvis): string {
  return `${nomSujet(t, invitation.subjectType)}${invitation.subjectDisplayName ? ` — ${invitation.subjectDisplayName}` : ''}`
}

/** « Séjour de 11 mois, terminé le 31/08/2026 » — ou « Relation depuis le … » quand elle court encore. */
function sousTitreInvitation(t: Textes, langue: Langue, invitation: InvitationAvis): string {
  const mois = dureeEnMois(invitation.relationStartedOn, invitation.relationEndedOn)

  if (invitation.relationEndedOn !== null) {
    return interpoler(t.avisSejourTermine, { n: mois, date: jour(langue, invitation.relationEndedOn) }, langue)
  }

  return interpoler(t.avisRelationDepuis, { date: jour(langue, invitation.relationStartedOn) })
}

function sousTitreDepot(t: Textes, langue: Langue, invitation: InvitationAvis): string {
  const avis = invitation.myReview

  if (avis === null || avis.submittedAt === null) {
    return sousTitreInvitation(t, langue, invitation)
  }

  const depose = interpoler(t.avisDeposeLe, { date: jour(langue, avis.submittedAt) })

  return avis.publishedAt === null ? depose : `${depose} · ${interpoler(t.avisPublieLe, { date: jour(langue, avis.publishedAt) })}`
}

function dureeEnMois(debut: string, fin: string | null): number {
  const a = new Date(debut)
  const b = fin === null ? new Date() : new Date(fin)

  return Math.max(0, (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()))
}

function tonDepot(invitation: InvitationAvis): 'succes' | 'attention' | 'danger' | undefined {
  switch (invitation.status) {
    case 'Submitted':
      return invitation.myReview?.publishedAt === null && invitation.isBilateral ? 'attention' : 'succes'
    case 'Declined':
    case 'Expired':
      return undefined
    default:
      return undefined
  }
}

function nomSujet(t: Textes, nature: NatureSujet): string {
  switch (nature) {
    case 'Property':
      return t.avisSujetLogement
    case 'Landlord':
      return t.avisSujetProprietaire
    case 'Tenant':
      return t.avisSujetLocataire
    case 'Roommate':
      return t.avisSujetColocataire
    case 'Household':
      return t.avisSujetFoyer
    case 'Agency':
      return t.avisSujetAgence
  }
}

/** Qui parle d'un avis reçu : si je suis noté comme locataire, l'auteur est le propriétaire, et inversement. */
function nomAuteur(t: Textes, natureDuSujet: NatureSujet): string {
  switch (natureDuSujet) {
    case 'Tenant':
      return t.avisAuteurProprietaire
    case 'Landlord':
      return t.avisAuteurLocataire
    case 'Roommate':
      return t.avisAuteurColocataire
    default:
      return t.avisNombreUn
  }
}

function libelleStatut(t: Textes, invitation: InvitationAvis): string {
  switch (invitation.status) {
    case 'Submitted':
      // Le double aveugle produit un délai : le dire est ce qui empêche de le prendre pour un bug.
      return invitation.myReview?.publishedAt !== null || !invitation.isBilateral ? t.avisPublie : t.avisEnAttente
    case 'Declined':
      return t.avisRefuse
    case 'Expired':
      return t.avisExpire
    default:
      return t.avisEnAttente
  }
}

function initiales(nom: string | null): string {
  if (nom === null) return '?'

  return nom
    .split(/\s+/)
    .filter((mot) => mot.length > 0)
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toLocaleUpperCase())
    .join('')
}
